import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter, NavLink } from 'react-router-dom';
import classnames from 'classnames';
import { Icon, useAuth, Button } from '@iq/react-components';
import {
  pollingIntervals as periods,
  getPollingInterval,
  setPollingInterval,
  getPollingActive,
} from '../../../bundles/application';
import {
  getIsTenantOrSuperAdmin,
  getIsContributorOrAdmin,
  hasDocumentationReadPermission,
} from '../../../bundles/auth';
import { getLinkedSources } from '../../../bundles/sources';
import { getAllIntegrations } from '../../../bundles/integrations';
import { replacer } from '../../../utils';

const openSourceLink = (source) => {
  window.open(
    source.options.siteSourceLink
      .replace('<domain>', source.options.domain)
      .replace('<substationId>', source.options.thirdPartyId),
    '_blank'
  );
};

const HeaderSettings = ({ site, noSiteView }) => {
  const dispatch = useDispatch();
  const { realm } = useAuth();

  const pollingInterval = useSelector(getPollingInterval);
  const isPolling = useSelector(getPollingActive);
  const sources = useSelector(getLinkedSources);
  const integrations = useSelector(getAllIntegrations);
  const isTenantAdmin = useSelector((state) => getIsTenantOrSuperAdmin(state, realm));
  const isContributorOrAdmin = useSelector((state) => getIsContributorOrAdmin(state, site));
  const canReadDocumentation = useSelector(hasDocumentationReadPermission);

  const [open, setIsOpen] = useState(false);
  const [showIntervals, setShowIntervals] = useState(false);
  const ref = useRef(null);

  const classes = classnames('header-settings-component', { open });
  const spinningClass = classnames({ isPolling });

  const { integrationOptions } = site || {};

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setTimeout(() => {
          setIsOpen(false);
        }, 0);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleOpen = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setIsOpen((previous) => !previous);
  };

  const handleToggleIntervals = () => {
    setShowIntervals((previous) => !previous);
  };

  const handleSelectInterval = (interval) => {
    dispatch(setPollingInterval(interval));
    handleToggleIntervals();
  };

  const onIntegrationLink = (event, link) => {
    handleToggleOpen(event);
    window.open(link, '_blank');
  };

  if (noSiteView && !isTenantAdmin && !canReadDocumentation) return null;

  return (
    <div
      data-testid="header-settings"
      className={classes}
      ref={ref}
    >
      <div>
        <Button
          design="text"
          tooltip="More...  "
          className="toggle-container"
          onClick={handleToggleOpen}
        >
          <Icon
            icon="he-moreoptionshorizontal"
            size="s"
          />
        </Button>
      </div>
      <div className="content">
        <div className="choices">
          {site && !noSiteView && (
            <>
              <div
                className="choice"
                onClick={(event) => {
                  event.stopPropagation();
                  handleToggleIntervals();
                }}
              >
                <Icon
                  icon="he-load"
                  className={spinningClass}
                />
                Polling interval
                {(pollingInterval !== undefined || pollingInterval !== null) &&
                  ` - ${periods.find((p) => p.value === pollingInterval).label}`}
              </div>

              {showIntervals && (
                <div className="sub-choices">
                  {periods.map(({ label, value }) => (
                    <div
                      key={value}
                      className={`sub-choice ${pollingInterval === value ? 'selected' : ''}`}
                      onClick={() => handleSelectInterval(value)}
                      role="button"
                    >
                      {label}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {site && !noSiteView && (
            <NavLink
              className="choice"
              title="Support"
              exact
              to={{
                pathname: `/sites/${site.id}/support/`,
                search: globalThis.location.search,
              }}
            >
              <Icon icon="he-customer-support" />
              <span>Support</span>
            </NavLink>
          )}

          {isContributorOrAdmin && !noSiteView && (
            <NavLink
              className="choice"
              title="Administration"
              exact
              to={{
                pathname: `/sites/${site.id}/admin/`,
                search: globalThis.location.search,
              }}
            >
              <Icon icon="he-settings" />
              <span>Administration</span>
            </NavLink>
          )}

          {!noSiteView && integrationOptions
            ? Object.entries(integrationOptions).map(([k, v]) => {
                const integration = integrations.find((index) => index.id === k);
                const { endpoint, enabled } = integration?.configuration?.deeplinks?.siteLink || {};
                let link;
                if (endpoint && enabled) {
                  try {
                    link = endpoint.replaceAll(/({\w+})/g, (match) => replacer(match, v));
                    return (
                      <div
                        key={k}
                        className="choice"
                        onClick={(event) => onIntegrationLink(event, link)}
                        role="button"
                      >
                        <Icon
                          icon="he-customer-support"
                          size="s"
                        />
                        {`Site Information (${integration.name})`}
                      </div>
                    );
                  } catch (error) {
                    console.error(error);
                    return null;
                  }
                }
                return null;
              })
            : null}

          {noSiteView
            ? null
            : sources
                .filter((source) => source.options.siteSourceLink)
                .map((source) => (
                  <div
                    key={source.id}
                    className="choice"
                    onClick={() => openSourceLink(source)}
                    role="button"
                  >
                    <Icon
                      icon="he-customer-support"
                      size="s"
                    />
                    Detailed Site Information
                  </div>
                ))}

          {canReadDocumentation && (
            <NavLink
              className="choice"
              title="API Docs"
              exact
              to={{
                pathname: `/api-docs`,
                search: globalThis.location.search,
              }}
            >
              {!noSiteView && <Icon icon="blank" />}
              <span>API documentation</span>
            </NavLink>
          )}
          {isTenantAdmin && noSiteView && (
            <>
              <NavLink
                className="choice"
                title="Tenant Administration"
                exact
                to={{
                  pathname: `/tenant-administration/users`,
                  search: globalThis.location.search,
                }}
              >
                <span>Tenant Administration</span>
              </NavLink>

              {/* <NavLink
                className="choice"
                title="Integrations"
                exact
                to={{
                  pathname: `/integrations/`,
                  search: window.location.search,
                }}
              >
                <span>Integrations</span>
              </NavLink> */}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(HeaderSettings);
